import './styles/Billing.scss';

import { CircularProgress } from '@mui/material';
import { useBooleanFlagValue } from '@openfeature/react-sdk';
import { BillingPermissions, PermissionEnum } from '@thro-types/access-handler';
import { User } from '@thro-types/users';

import { lazy, Suspense, useLayoutEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { Route, Routes, useNavigate } from 'react-router';

import PrivateRoute from 'app/components/Util/PrivateRoute';
import { translations } from 'locales/i18n';
import Roles from 'utils/enums/Roles';
import { FEATURE_FLAGS } from 'utils/featureFlags';
import { useAuthContext } from 'utils/hooks/useAuthContext';

import { AlertActions } from '../Dashboard/Alert/slice';
import { BillingSettings } from './BillingSettings';
import { CustomerDetailPage } from './Customer/CustomerDetailPage';
import { CustomerDrawer } from './Customer/CustomerDrawer/CustomerDrawer';
import { CustomerOverview } from './Customer/CustomerOverview';
import { ApproveInvoicesView } from './FinanceDepartment/ApproveInvoices';
import { ExportInvoices } from './FinanceDepartment/ExportInvoices';
import { IndexRoute } from './MyInvoices';

const MyInvoicesList = lazy(() => import('./MyInvoices/list'));

export const Billing = ({
  currentUser,
  userPermission
}: {
  currentUser: User;
  userPermission: PermissionEnum[];
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isBillingEnabled = useBooleanFlagValue(FEATURE_FLAGS.BILLING.ENABLED, true);

  useLayoutEffect(() => {
    if (!isBillingEnabled) {
      navigate('/');
      dispatch(AlertActions.warning(t(translations.common.areaCurrentlyDisabled())));
    }
  }, [dispatch, isBillingEnabled, navigate, t]);

  return (
    <Suspense
      fallback={
        <div
          style={{
            flex: 1,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '80vh'
          }}
        >
          <CircularProgress />
        </div>
      }
    >
      <CustomerDrawer />
      <Routes>
        <Route
          index
          element={
            <PrivateRoute
              requiredPermission={BillingPermissions.BILLING_INVOICE_READ}
              userPermissions={userPermission}
              user={currentUser}
            >
              <MyInvoicesList />
            </PrivateRoute>
          }
        />
        <Route
          path="invoices/*"
          element={
            <PrivateRoute
              requiredPermission={BillingPermissions.BILLING_INVOICE_READ}
              userPermissions={userPermission}
              user={currentUser}
            >
              <IndexRoute />
            </PrivateRoute>
          }
        />
        <Route
          path="customers"
          element={
            <PrivateRoute
              requiredPermission={BillingPermissions.BILLING_CUSTOMER_READ}
              userPermissions={userPermission}
              user={currentUser}
            >
              <CustomerOverview />
            </PrivateRoute>
          }
        />
        <Route
          path="customers/:id"
          element={
            <PrivateRoute
              requiredPermission={BillingPermissions.BILLING_CUSTOMER_READ}
              userPermissions={userPermission}
              user={currentUser}
            >
              <CustomerDetailPage />
            </PrivateRoute>
          }
        />
        <Route
          path="finance"
          element={
            <PrivateRoute
              requiredPermission={BillingPermissions.BILLING_FINANCE_READ}
              userPermissions={userPermission}
              user={currentUser}
            >
              <ApproveInvoicesView />
            </PrivateRoute>
          }
        />
        <Route
          path="export"
          element={
            <PrivateRoute
              requiredPermission={BillingPermissions.BILLING_FINANCE_READ}
              userPermissions={userPermission}
              user={currentUser}
            >
              <ExportInvoices />
            </PrivateRoute>
          }
        />
        <Route
          path="billing-settings"
          element={
            <PrivateRoute
              requiredPermission={BillingPermissions.BILLING_FINANCE_READ}
              userPermissions={userPermission}
              user={currentUser}
            >
              <BillingSettings />
            </PrivateRoute>
          }
        />
      </Routes>
    </Suspense>
  );
};

export default Billing;
